import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  Home as HomeIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';

const emptyForm = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  password: '',
  confirm_password: '',
  remember_me: true,
  learning_paths: []
};

const AuthModal = ({ open = true, onClose, initialMode = 'register' }) => {
  const { loading, login, register, user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setError('');
    }
  }, [initialMode, open]);

  const update = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setError('');
  };

  const switchMode = (_, value) => {
    setMode(value);
    setError('');
  };

  const submit = async (event) => {
    event.preventDefault();
    if (mode === 'register' && form.password !== form.confirm_password) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      if (mode === 'register') {
        const { confirm_password: _confirmPassword, ...registerPayload } = form;
        await register(registerPayload);
        onClose?.();
        navigate('/my-learning');
      } else {
        await login({ email: form.email, password: form.password, remember_me: form.remember_me });
        onClose?.();
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (user) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1, pr: 7 }}>
        {mode === 'register' ? 'Save your learning progress' : 'Welcome back'}
        {onClose && (
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form" onSubmit={submit}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {mode === 'register'
                ? 'Create an account. Your cohort and track will be assigned by the program admin.'
                : 'Sign in to continue where you left off.'}
            </Typography>
            <Tabs value={mode} onChange={switchMode} variant="fullWidth" sx={{ mb: 2 }}>
              <Tab value="register" label="Create account" />
              <Tab value="login" label="Sign in" />
            </Tabs>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}
              {mode === 'register' && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField label="First name" value={form.first_name} onChange={update('first_name')} required fullWidth />
                  <TextField label="Last name" value={form.last_name} onChange={update('last_name')} required fullWidth />
                </Stack>
              )}
              <TextField label="Email" type="email" value={form.email} onChange={update('email')} required fullWidth />
              {mode === 'register' && (
                <TextField label="Phone number" value={form.phone} onChange={update('phone')} fullWidth />
              )}
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={update('password')}
                required
                inputProps={{ minLength: 8 }}
                helperText={mode === 'register' ? 'Use at least 8 characters.' : ''}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        onClick={() => setShowPassword((current) => !current)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                fullWidth
              />
              {mode === 'register' && (
                <TextField
                  label="Confirm password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={form.confirm_password}
                  onChange={update('confirm_password')}
                  required
                  error={Boolean(form.confirm_password) && form.password !== form.confirm_password}
                  helperText={
                    Boolean(form.confirm_password) && form.password !== form.confirm_password
                      ? 'Passwords must match.'
                      : ''
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                          onClick={() => setShowConfirmPassword((current) => !current)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  fullWidth
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.remember_me}
                    onChange={(event) => setForm((current) => ({ ...current, remember_me: event.target.checked }))}
                  />
                }
                label="Remember me on this device"
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting}
                endIcon={mode === 'register' ? <ArrowForwardIcon /> : null}
              >
                {submitting
                  ? 'Please wait…'
                  : mode === 'register'
                    ? 'Create account'
                    : 'Sign in'}
              </Button>

              <Button
                component={RouterLink}
                to="/"
                onClick={onClose}
                variant="text"
                size="small"
                startIcon={<HomeIcon fontSize="small" />}
                sx={{ color: 'text.secondary', alignSelf: 'center' }}
              >
                Back to homepage
              </Button>
            </Stack>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
